// Agregações multi-janela a partir da view normalizada v_ads_campaigns_daily.
// Antes: lia o payload jsonb completo (45MB/30d). Agora: 385KB.
//
// Reservas + receita REAIS vêm do Niara (v_niara_daily). Distribuição entre
// canais e campanhas por share-of-spend.

export interface NiaraDay {
  date: string;
  reservations: number;
  revenue_brl: number;
}

export interface CampaignDailyRow {
  channel: string;
  date: string;
  campaign_id: string;
  campaign_name: string;
  objective: string;
  status: string;
  spend_brl: number;
  impressions: number;
  clicks: number;
  ctr_pct: number;
  cpm_brl: number;
  cpc_brl: number;
  conversions_pixel: number;
  value_pixel: number;
  roas_pixel: number;
  channel_spend: number;
  channel_revenue: number;
}

export interface CampaignWindowed {
  channel: string;
  campaign_id: string;
  campaign_name: string;
  objective: string;
  spend_24h: number;  revenue_24h: number;  purchases_24h: number;  roas_24h: number;
  spend_3d: number;   revenue_3d: number;   purchases_3d: number;   roas_3d: number;
  spend_7d: number;   revenue_7d: number;   purchases_7d: number;   roas_7d: number;
  spend_30d: number;  revenue_30d: number;  purchases_30d: number;  roas_30d: number;
  trend: "up" | "down" | "stable";
}

function aggregateWindow(
  rows: CampaignDailyRow[],
  dates: Set<string>,
  niaraByDate: Map<string, NiaraDay>,
  totalSpendByDate: Map<string, number>,
): Map<string, { spend: number; revenue: number; purchases: number; name: string; objective: string; channel: string }> {
  const map = new Map<string, { spend: number; revenue: number; purchases: number; name: string; objective: string; channel: string }>();
  for (const r of rows) {
    if (!dates.has(r.date)) continue;
    const totalDay = totalSpendByDate.get(r.date) ?? 0;
    const channelShareOfDay = totalDay > 0 ? r.channel_spend / totalDay : 0;
    const campaignShareOfChannel = r.channel_spend > 0 ? r.spend_brl / r.channel_spend : 0;
    const niara = niaraByDate.get(r.date);
    const revenue = niara ? niara.revenue_brl * channelShareOfDay * campaignShareOfChannel : 0;
    const purchases = niara ? niara.reservations * channelShareOfDay * campaignShareOfChannel : 0;
    const key = `${r.channel}::${r.campaign_id}`;
    const slot = map.get(key) ?? { spend: 0, revenue: 0, purchases: 0, name: r.campaign_name, objective: r.objective, channel: r.channel };
    slot.spend += r.spend_brl;
    slot.revenue += revenue;
    slot.purchases += purchases;
    map.set(key, slot);
  }
  return map;
}

export function aggregateCampaigns(rows: CampaignDailyRow[], niara: NiaraDay[]): { rows: CampaignWindowed[]; lastDate: string | null } {
  if (rows.length === 0) return { rows: [], lastDate: null };
  const niaraByDate = new Map(niara.map((n) => [n.date, n]));
  const dates = [...new Set(rows.map((r) => r.date))].sort((a, b) => b.localeCompare(a));
  const lastDate = dates[0] ?? null;

  // Total spend por dia = soma do channel_spend de cada (channel, date) único
  const seen = new Set<string>();
  const totalSpendByDate = new Map<string, number>();
  for (const r of rows) {
    const k = `${r.channel}::${r.date}`;
    if (seen.has(k)) continue;
    seen.add(k);
    totalSpendByDate.set(r.date, (totalSpendByDate.get(r.date) ?? 0) + r.channel_spend);
  }

  const m24 = aggregateWindow(rows, new Set(dates.slice(0, 1)),  niaraByDate, totalSpendByDate);
  const m3  = aggregateWindow(rows, new Set(dates.slice(0, 3)),  niaraByDate, totalSpendByDate);
  const m7  = aggregateWindow(rows, new Set(dates.slice(0, 7)),  niaraByDate, totalSpendByDate);
  const m30 = aggregateWindow(rows, new Set(dates.slice(0, 30)), niaraByDate, totalSpendByDate);

  const allKeys = new Set<string>();
  for (const m of [m24, m3, m7, m30]) for (const k of m.keys()) allKeys.add(k);

  const out: CampaignWindowed[] = [];
  for (const key of allKeys) {
    const s24 = m24.get(key); const s3 = m3.get(key); const s7 = m7.get(key); const s30 = m30.get(key);
    const ref = s30 ?? s7 ?? s3 ?? s24!;
    const roas24 = (s24?.spend ?? 0) > 0 ? s24!.revenue / s24!.spend : 0;
    const roas3  = (s3?.spend ?? 0)  > 0 ? s3!.revenue / s3!.spend  : 0;
    const roas7  = (s7?.spend ?? 0)  > 0 ? s7!.revenue / s7!.spend  : 0;
    const roas30 = (s30?.spend ?? 0) > 0 ? s30!.revenue / s30!.spend : 0;
    const trend: "up" | "down" | "stable" =
      roas24 === 0 || roas7 === 0 ? "stable" :
      roas24 > roas7 * 1.10 ? "up" :
      roas24 < roas7 * 0.90 ? "down" : "stable";
    out.push({
      channel: ref.channel,
      campaign_id: key.split("::")[1] ?? "",
      campaign_name: ref.name,
      objective: ref.objective,
      spend_24h: s24?.spend ?? 0, revenue_24h: s24?.revenue ?? 0, purchases_24h: s24?.purchases ?? 0, roas_24h: roas24,
      spend_3d:  s3?.spend  ?? 0, revenue_3d:  s3?.revenue  ?? 0, purchases_3d:  s3?.purchases  ?? 0, roas_3d:  roas3,
      spend_7d:  s7?.spend  ?? 0, revenue_7d:  s7?.revenue  ?? 0, purchases_7d:  s7?.purchases  ?? 0, roas_7d:  roas7,
      spend_30d: s30?.spend ?? 0, revenue_30d: s30?.revenue ?? 0, purchases_30d: s30?.purchases ?? 0, roas_30d: roas30,
      trend,
    });
  }
  // Mostra apenas campanhas que tiveram gasto no período (estiveram ativas no mês).
  // Campanhas com spend_30d = 0 são pausadas/sem alcance e poluem a lista.
  const active = out.filter((r) => r.spend_30d > 0.01);
  active.sort((a, b) => b.spend_30d - a.spend_30d);
  return { rows: active, lastDate };
}

export interface WindowTotals { spend: number; revenue: number; purchases: number; roas: number; cpa: number; aov: number; days: number; impressions: number; clicks: number; ctr: number; cpm: number }

// Versão leve: usa account_kpis dos snapshots (sem o payload jsonb).
export interface DailySnapshotLite { channel: string; date: string; spend_brl: number; impressions: number; clicks: number }

// `filteredSnaps`: snapshots já filtrados pelo canal selecionado (ou todos se "all").
// `allSnaps`: TODOS os snapshots — necessário pra calcular share-of-spend
// quando há filtro de canal, e atribuir corretamente as compras/receita Niara.
export function totalsByWindow(
  filteredSnaps: DailySnapshotLite[],
  allSnaps: DailySnapshotLite[],
  niara: NiaraDay[],
): { d24: WindowTotals; mm3: WindowTotals; mm7: WindowTotals; mm15: WindowTotals; d30: WindowTotals } {
  const dates = [...new Set(allSnaps.map((s) => s.date))].sort((a, b) => b.localeCompare(a));
  const niaraByDate = new Map(niara.map((n) => [n.date, n]));
  // Soma total de gasto por dia (todos canais) — denominador do share
  const totalSpendByDate = new Map<string, number>();
  for (const s of allSnaps) totalSpendByDate.set(s.date, (totalSpendByDate.get(s.date) ?? 0) + s.spend_brl);
  // Soma de gasto filtrado por dia (numerador do share)
  const filteredSpendByDate = new Map<string, number>();
  const filteredImprByDate = new Map<string, number>();
  const filteredClicksByDate = new Map<string, number>();
  for (const s of filteredSnaps) {
    filteredSpendByDate.set(s.date, (filteredSpendByDate.get(s.date) ?? 0) + s.spend_brl);
    filteredImprByDate.set(s.date, (filteredImprByDate.get(s.date) ?? 0) + s.impressions);
    filteredClicksByDate.set(s.date, (filteredClicksByDate.get(s.date) ?? 0) + s.clicks);
  }
  const maps = { niaraByDate, filteredSpendByDate, totalSpendByDate, filteredImprByDate, filteredClicksByDate };

  return {
    d24:  totalsFor(dates.slice(0, 1),  maps),
    mm3:  totalsFor(dates.slice(0, 3),  maps),
    mm7:  totalsFor(dates.slice(0, 7),  maps),
    mm15: totalsFor(dates.slice(0, 15), maps),
    d30:  totalsFor(dates.slice(0, 30), maps),
  };
}

interface WindowMaps {
  niaraByDate: Map<string, NiaraDay>;
  filteredSpendByDate: Map<string, number>;
  totalSpendByDate: Map<string, number>;
  filteredImprByDate: Map<string, number>;
  filteredClicksByDate: Map<string, number>;
}

function totalsFor(dates: string[], m: WindowMaps): WindowTotals {
  let spend = 0, purchases = 0, revenue = 0, impressions = 0, clicks = 0;
  for (const d of dates) {
    const fSpend = m.filteredSpendByDate.get(d) ?? 0;
    const tSpend = m.totalSpendByDate.get(d) ?? 0;
    spend += fSpend;
    impressions += m.filteredImprByDate.get(d) ?? 0;
    clicks += m.filteredClicksByDate.get(d) ?? 0;
    const n = m.niaraByDate.get(d);
    if (!n) continue;
    // Quando "all" filtrado = total, share = 1 → soma Niara cheio.
    // Quando filtra por canal, share < 1 → atribui proporcional.
    const share = tSpend > 0 ? fSpend / tSpend : 0;
    purchases += n.reservations * share;
    revenue += n.revenue_brl * share;
  }
  return {
    spend, revenue, purchases,
    roas: spend > 0 ? revenue / spend : 0,
    cpa: purchases > 0 ? spend / purchases : 0,
    aov: purchases > 0 ? revenue / purchases : 0,
    days: dates.length,
    impressions, clicks,
    ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
    cpm: impressions > 0 ? (spend / impressions) * 1000 : 0,
  };
}
